import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Snackbar, Alert, Button, IconButton, Select, MenuItem } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchSalesNewInstallations, updateSalesNewInstallation, deleteSalesNewInstallation, emailSalesNewInstallationPdf, fetchCustomers, fetchSolutions } from "../services/ApiService";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import SendEmailConfirmDialog from '../components/SendEmailConfirmDialog';
import CircularProgress from '@mui/material/CircularProgress';
import DataTable from '../components/DataTable';
import BulkUploadSales from '../components/BulkUploadSales';

export function SalesNewInstallation() {
    const navigate = useNavigate();
    const location = useLocation();
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' }); // Snackbar state
    const [sendingEmailId, setSendingEmailId] = React.useState(null);
    const [haulierOptions, setHaulierOptions] = React.useState([]);
    const [devicesOptions, setDevicesOptions] = React.useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [sendConfirmOpen, setSendConfirmOpen] = React.useState(false);
    const [selectedActionSaleId, setSelectedActionSaleId] = React.useState(null);

    // Open delete confirmation
    const handleDeleteClick = (saleId) => {
        setSelectedActionSaleId(saleId);
        setDeleteConfirmOpen(true);
    };

    // Confirm delete
    const confirmDelete = async () => {
        if (selectedActionSaleId) {
            await handleDelete(selectedActionSaleId);
        }
        setDeleteConfirmOpen(false);
        setSelectedActionSaleId(null);
    };

    // Open send email confirmation
    const handleSendClick = (saleId) => {
        setSelectedActionSaleId(saleId);
        setSendConfirmOpen(true);
    };

    // Confirm send email
    const confirmSendEmail = async () => {
        if (selectedActionSaleId) {
            await handleSendEmail(selectedActionSaleId);
        }
        setSendConfirmOpen(false);
        setSelectedActionSaleId(null);
    };


    const fetchSales = async () => {
        try {
            const response = await fetchSalesNewInstallations();
            setSalesData(response.data);
            showSnackbar('Sales fetched successfully', 'success');
        } catch (error) {
            const errorMessage =
                error.response?.statusText || error.message || 'Error fetching sales';
            showSnackbar(`Failed to fetch sales: ${errorMessage}`, 'error');
        }
    };
    const fetchDevices = async () => {
        try {
            const response = await fetchSolutions();
            const deviceDict = response.data.reduce((acc, item) => {
                acc[item.id] = item.name;
                return acc;
            }, {});
            setDevicesOptions(deviceDict)
            showSnackbar('Hauliers fetched successfully', 'success');
        } catch (error) {
            const errorMessage =
                error.response?.statusText || error.message || 'Error fetching hauliers';
            showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
        }
    };
    const handleSendEmail = async (saleId) => {
        setSendingEmailId(saleId);

        try {
            await emailSalesNewInstallationPdf(saleId, {});
            alert("Email sent successfully!");
        } catch (error) {
            console.error("Email error:", error);
            alert("Failed to send email.");
        } finally {
            setSendingEmailId(null);
        }
    };
    const fetchHauliers = async () => {
        try {
            const response = await fetchCustomers();
            const haulierDict = response.data.reduce((acc, item) => {
                acc[item.id] = item.name;
                return acc;
            }, {});
            setHaulierOptions(haulierDict)
            showSnackbar('Hauliers fetched successfully', 'success');
        } catch (error) {
            const errorMessage =
                error.response?.statusText || error.message || 'Error fetching hauliers';
            showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
        }
    };
    const handleStatusChange = async (saleId, newStatus, sale) => {
        const updatedSale = { ...sale, status: newStatus };

        try {
            await updateSalesNewInstallation(saleId, updatedSale);
            // Update state in frontend
            setSalesData((prevData) =>
                prevData.map((item) =>
                    item.id === saleId ? { ...item, status: newStatus } : item
                )
            );
            showSnackbar('Status updated successfully', 'success');
        } catch (error) {
            console.error('Failed to update status:', error);
            showSnackbar('Failed to update status', 'error');
        }
    };
    React.useEffect(() => {
        fetchSales();
        fetchHauliers()
        fetchDevices()
    }, []);

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };
    const [salesData, setSalesData] = React.useState([])
    React.useEffect(() => {
        localStorage.setItem('sales', JSON.stringify(salesData));
        console.log(salesData, 'salesData');
    }, [salesData]);

    React.useEffect(() => {
        if (location.state?.successMessage) {
            showSnackbar(location.state.successMessage, 'success');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (id) => {
        try {
            await deleteSalesNewInstallation(id);
            showSnackbar("Customer deleted successfully", "success");
            await fetchSales();
        } catch (error) {
            showSnackbar("Failed to delete customer", "error");
            console.error(error);
        }
    };
    const handleExport = () => {
        const doc = new jsPDF('l', 'mm', 'a3'); // landscape, A3 size

        // Title
        doc.setFontSize(18);
        doc.text('Sales Report', 14, 16);

        // Define Columns
        const columns = [
            'Sale #',
            'Customer',
            'Corporate',
            'POC Name',
            'POC Contact',
            'Solution',
            'Features',
            'Plan',
            'Unit',
            'Document',
            'Production Status',
            'Customer Support Status',
            'Collection Recovery Status',
            'Close At',
        ]; // ✅ removed "Action" for export

        // Table Data
        const tableData = salesData.map((record, index) => [
            index + 1, // Sale #
            haulierOptions[record.customer] || record.customer, // Customer
            record.corporate,
            record.poc_name,
            record.poc_contact,
            record.solution,
            record.features,
            record.plan,
            record.unit,
            record.document,
            record.production_status,
            record.customer_support_status,
            record.sales_coordinator_status,
            record.status === "Complete"
                ? record.updated_at.slice(0, 19).replace("T", " ")
                : "", // Close At
        ]);

        // Generate table
        doc.autoTable({
            head: [columns],
            body: tableData,
            startY: 30,
            styles: {
                fontSize: 9,
                cellPadding: 2,
            },
            columnStyles: {
                0: { cellWidth: 15 },  // Sale #
                1: { cellWidth: 15 },  // Customer
                2: { cellWidth: 35 },  // Corporate
                3: { cellWidth: 35 },  // POC Name
                4: { cellWidth: 30 },  // POC Contact
                5: { cellWidth: 40 },  // Solution
                6: { cellWidth: 40 },  // Features
                7: { cellWidth: 25 },  // Plan
                8: { cellWidth: 25 },  // Unit
                9: { cellWidth: 40 },  // Document
                10: { cellWidth: 20 }, // Production Status
                11: { cellWidth: 20 }, // Customer Support Status
                12: { cellWidth: 20 }, // Collection Recovery Status
                13: { cellWidth: 20 }, // Close At
            },
            theme: 'grid',
            didDrawPage: function () {
                doc.setFontSize(18);
                doc.text('Sales Report', 14, 16);
            },
        });

        doc.save('Sales Report.pdf');
    };
    const COLUMNS = [
        { key: 'id', label: 'Sale #' },
        { key: 'customer', label: 'Customer', render: (s) => haulierOptions[s.customer] },
        { key: 'corporate', label: 'Corporate' },
        { key: 'poc_name', label: 'POC Name' },
        { key: 'poc_contact', label: 'POC Contact' },
        { key: 'solution', label: 'Solution', render: (s) => devicesOptions[s.solution] },
        { key: 'features', label: 'Features', render: (s) => <div style={{ maxHeight: 80, overflow: 'auto' }}>{s.features}</div> },
        { key: 'plan', label: 'Plan' },
        { key: 'unit', label: 'Unit', render: (s) => <div style={{ maxHeight: 80, overflow: 'auto' }}>{s.unit}</div> },
        { key: 'document_url', label: 'Document', render: (s) => s.document_url ? <a href={`https://crm-backend.monit.tech/${s.document_url}`} download target="_blank" rel="noopener noreferrer">Download</a> : <span style={{ color: '#999' }}>Not Available</span> },
        { key: 'production_status', label: 'Production Status' },
        { key: 'customer_support_status', label: 'CS Status' },
        { key: 'sales_coordinator_status', label: 'Collection Recovery Status' },
        { key: 'close_at', label: 'Close At', render: (s) => s.status === 'Complete' ? s.updated_at?.slice(0, 19).replace('T', ' ') : '' },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <DataTable
                title="Sales New Installation"
                columns={COLUMNS}
                rows={salesData}
                toolbarActions={
                    <>
                        <BulkUploadSales />
                        <Button variant="contained" color="primary" onClick={() => navigate('/sales-new-installation/add')}>Add Sale</Button>
                        <Button variant="contained" color="primary" onClick={handleExport}>Export to PDF</Button>
                    </>
                }
                renderActions={(sale) => (
                    <>
                        <IconButton color="secondary" size="small" onClick={() => navigate(`/sales-new-installation/edit/${sale.id}`, { state: { record: sale } })} sx={{ p: '6px' }} disabled={sale.status === 'Complete'}><EditIcon /></IconButton>
                        <IconButton color="error" size="small" onClick={() => handleDeleteClick(sale.id)} sx={{ p: '6px', ml: '4px' }} disabled={sale.status === 'Complete'}><DeleteIcon /></IconButton>
                        <IconButton color="primary" size="small" onClick={() => handleSendClick(sale.id)} sx={{ p: '6px', ml: '4px' }} disabled={sendingEmailId === sale.id}>
                            {sendingEmailId === sale.id ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        </IconButton>
                        <Select value={sale.status} onChange={(e) => handleStatusChange(sale.id, e.target.value, sale)} size="small" sx={{ ml: 1, minWidth: 100 }}>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Complete">Complete</MenuItem>
                        </Select>
                    </>
                )}
            />
            <DeleteConfirmDialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} onConfirm={confirmDelete} />
            <SendEmailConfirmDialog open={sendConfirmOpen} onClose={() => setSendConfirmOpen(false)} onConfirm={confirmSendEmail} />
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
}

